<template>
    <modal-centered-base
    fullScreenDialog
    preventClickOutsideClose
    sessionExpired
    titleIcon="logout"
    rightButtonIcon="arrow-right-full"
    secondSideModalLayer
    :triggerClose="triggerClose"
    :title="t('modals.sessionExpiredModal.expiredSession')"
    :leftButtonText="t('modals.sessionExpiredModal.ignoreButton')"
    :rightButtonText="t('modals.sessionExpiredModal.continueButton')"
    @hideMe="logoutHandler"
    @rightButtonClicked="validateInput">
        <div class="text">{{t('modals.sessionExpiredModal.expiredSessionDescription')}}</div>
        <!-- Input -->
        <input-password
        :params="passwordInputParams"
        :inputValue="password"
        @input="password = $event"
        @onEnter="validateInput"/>
        <!-- Description -->
        <div v-if="showBiometricOption" class="biometric-button-container">
            <div class="divider-container">{{t('modals.sessionExpiredModal.dividerText')}}</div>
            <button-secondary
            :icon="getMobileBiometric === 'face' ? 'face-id' : 'fingerprint'"
            :label="getMobileBiometric === 'face' ? t('modals.sessionExpiredModal.useIosFace') : t('modals.sessionExpiredModal.biometricButton')"
            @clicked="requestBiometricChallenge"/>
        </div>
        <!-- Loader -->
        <loader-generic v-if="showLoader"
        :insideCenteredModal="!getMobileAppState"
        :fullSize="!getMobileAppState"
        :isOverlay="getMobileAppState"/>
    </modal-centered-base>
    <!-- Feedback Toast -->
    <toast-generic
    v-if="showToast"
    toastType="error"
    :feedbackText="feedbackToastText"
    @hideMe="showToast = false"/>
    <!-- Loader -->
    <loader-generic v-if="showOverlayLoader" isOverlay/>
</template>

<script setup>
/* Components */
import ModalCenteredBase from '@/components/generic/modals/centered/Base'
import InputPassword from '@/components/generic/inputs/Password'
import ButtonSecondary from '@/components/generic/buttons/Secondary'
import LoaderGeneric from '@/components/generic/loaders/Generic'
import ToastGeneric from '@/components/generic/toasts/Generic'
/* Vue */
import { ref, computed } from 'vue'
/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
/* Aux */
import { NUMBERS_ONLY } from '@/assets/constants/masks'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* mapActions,  mapGetters */
const { extendSession, updateSessionExpireState, sendExtensionBiometricChallenge, fetchBiometricChallenge, logout, forceLogout } = mapActions(moduleMapper.SESSION)
const { getRememberMeEntityInfo, getAllCustomerInfo, getMobileBiometric } = mapGetters(moduleMapper.SESSION)
const { getMobileAppState } = mapGetters()

const showLoader = ref(false)
const showOverlayLoader = ref(false)
const triggerClose = ref(false)
const password = ref('')

const passwordInputParams = ref({
    labelText: getMobileAppState.value ? t('modals.sessionExpiredModal.mobileInputLabel') : t('modals.sessionExpiredModal.webInputLabel'),
    placeholder: t('modals.sessionExpiredModal.webInputPlaceholder'),
    errorMessage: t('mandatoryFieldError'),
	errorState: false,
    mandatory: true,
	maxLength: 6,
	mask: NUMBERS_ONLY
})

/* Biometric Challenge response */
const biometricChallenge = ref(null)
const biometricChallengeResponse = ref(null)

const showBiometricOption = computed(() => {
    return getMobileAppState.value && getRememberMeEntityInfo.value && selectedAcession.value?.hasBiometricEnrolled
})

const selectedAcession = computed(() => {
    return getRememberMeEntityInfo.value.listOfAcessions.find(el => el.acessionNumber === getAllCustomerInfo.value.acessionNumber)
})

const validateInput = () => {
	passwordInputParams.value.errorState = !password.value.length
	if (password.value.length) requestSessionExtension()
}

/*************************************************
*                                                *
*                  Requests Logic              *
*                                                *
*************************************************/
const logoutHandler = async () => {
    showOverlayLoader.value = true

    try {
        await logout()
    } finally {
        forceLogout()
    }
}

const requestSessionExtension = async () => {
    showLoader.value = true

    const payload = {
        password: password.value
    }

    try {
        await extendSession(payload)
        updateSessionExpireState(false)
        triggerClose.value = true
        showLoader.value = false
    } catch (error) {
        showToastHandler(error.response && error.response.data ? error.response.data : { errorLevel: 'error' })
    }
}

/* Requesting Biometric Challenge for login */
const requestBiometricChallenge = async () => {
	showLoader.value = true
	const payload = {
		rememberMeToken: selectedAcession.value.rememberMeToken
	}

	try {
		const challenge = await fetchBiometricChallenge(payload)
		biometricChallenge.value = challenge.data.data
		showAnswerChallengeBiometricDialog()
	} catch (error) {
		showLoader.value = false
		showToastHandler(error.response && error.response.data ? error.response.data : { errorLevel: 'error' })
	}
}

const showAnswerChallengeBiometricDialog = () => {
	window.Fingerprint.loadBiometricSecret({
		title: t('modals.sessionExpiredModal.biometric.title'),
		description: t('modals.sessionExpiredModal.biometric.authenticationDescription'),
		cancelButtonTitle: t('modals.sessionExpiredModal.biometric.cancelButtonTitle'),
		disableBackup: true
	}, decryptChallenge, dismissChallengeBiometricDialog)
	showLoader.value = false
}

const dismissChallengeBiometricDialog = error => {
	if (error.code !== -108) {
        showToastHandler(error.response && error.response.data ? error.response.data : { errorLevel: 'error' })
	}
}

const decryptChallenge = privateKey => {
	const binaryData = Buffer.from(biometricChallenge.value.challenge, 'base64')
	const temp = JSON.parse(privateKey)

	crypto.subtle.importKey(
		'jwk',
		temp,
		{
			name: 'RSA-OAEP',
			modulusLength: 2048,
			publicExponent: new Uint8Array([1, 0, 1]), // 24 bit representation of 65537
			hash: { name: 'SHA-1' }
		},
		true,
		['decrypt']
	).then(
		(key) => {
			crypto.subtle.decrypt(
				{ name: 'RSA-OAEP' },
				key,
				binaryData
			).then(
				(data) => {
					var enc = new TextDecoder('utf-8')
					var clearText = enc.decode(data)

					biometricChallengeResponse.value = {
						rememberMeToken: selectedAcession.value.rememberMeToken,
						authenticationProcessId: biometricChallenge.value.loginProcessId,
						timestamp: clearText.split('|')[0],
						nonce: clearText.split('|')[1]
					}
					requestBiometricSessionExtension()
				}
			)
		}
	)
}

const requestBiometricSessionExtension = async () => {
    showLoader.value = true

    try {
        await sendExtensionBiometricChallenge(biometricChallengeResponse.value)
        updateSessionExpireState(false)
        triggerClose.value = true
        showLoader.value = false
    } catch (error) {
        showToastHandler(error.response && error.response.data ? error.response.data : { errorLevel: 'error' })
    }
}

/*************************************************
*                                                *
*               Feedback Toast Logic             *
*                                                *
*************************************************/
const showToast = ref(false)
const feedbackToastText = ref('')

const showToastHandler = response => {
    showLoader.value = false
    feedbackToastText.value = response.message || t('genericErrorMessage')
    showToast.value = true
}
</script>

<style lang="scss" scoped>
.text {
    margin-bottom: 16px;
}

.biometric-button-container {
    width: 100%;

    display: flex;
    flex-direction: column;

    .divider-container {
        width: 100%;
        margin: 24px 0;

        overflow: hidden;
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;

        @include H8-Regular;
        color: var(--secondary);
        text-transform: uppercase;

        &::before,
        &::after {
            content: "";

            height: 1px;
            display: inline-block;
            position: absolute;
            width: 50%;

            background-color: var(--secondary-light-2);
        }

        &::before {
            right: -18px;
        }

        &::after {
            left: -18px;
        }
    }
}
</style>
